import axios from '@/axios';
import HandleResponsesV2 from '@common/utils/handleApiResponsesV2';

const actions = {
  async filter({ commit }, filters) {
    try {
      let result = [];
      if (filters.page_number === 1) {
        commit('SET_LOADING', true);
      } else {
        commit('paginationCheck', true);
      }
      const params = filters;
      params.show = 'consumed_capacity,charges,lorry_receipts';
      result = await axios('/v2/transporter/payments', { params });

      if (result && result.data) {
        commit('SET_LIST', result.data.payments);
      }
      return result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    } finally {
      commit('paginationCheck', false);
      commit('SET_LOADING', false);
    }
  },

  async downloadExcel(_, filter) {
    try {
      const result = await axios({
        url: '/v2/transporter/payments/downloadExcel',
        method: 'GET',
        responseType: 'json',
        params: filter
      });
      return result;
    } catch (error) {
      return error;
    }
  },

  async uploadExcel(_, formData) {
    try {
      const result = await axios.put('/v2/transporter/payments/uploadExcel',
        formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result;
    } catch (error) {
      return error;
    }
  },

  async uploadStatus(_, params) {
    try {
      const result = await axios('v2/transporter/uploadStatus', { params });
      return result.data;
    } catch (error) {
      return error;
    }
  },

  async updateIndent({ commit }, data) {
    try {
      const result = await axios.patch(`/v2/transporter/paymentsInvoice/${data.id}`,
        { data: data.data });
      /* Adding success response constants */
      if (result && result.data === 'Success') {
        commit('updatePayments', result.updates.payments[0]);
      }
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async verifyInvoice(_, { invoiceId, irnNumber, invoiceDate }) {
    try {
      const result = await axios.put(`/v2/transporter/invoice/verify/${invoiceId}`, { data: { irnNumber, invoiceDate } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateInvoiceCharges(_, { invoiceId, payments }) {
    try {
      const result = await axios.put(`/v2/transporter/invoice/${invoiceId}`, { data: { payments } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateMultiPayments(_, data) {
    try {
      const result = await axios.patch('/v2/transporter/multiPayments/', { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateAttachment(_, { paymentId, formData }) {
    try {
      const result = await axios.post(`/v2/transporter/payments/${paymentId}/indent_attachments`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async deleteAttachment(_, { attachmentId }) {
    try {
      const result = await axios.delete(`/v2/transporter/payments/attachment/${attachmentId}`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateComment(_, params) {
    try {
      const result = await axios.post(`/v2/transporter/payment/${params.id}/comment`,
        { data: params.data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },
  async updateChargeComment(_, params) {
    try {
      const result = await axios.post(`/v2/transporter/payment/${params.id}/chargeComment`,
        { data: params.data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateCommentAttachment(_, { commentId, formData }) {
    try {
      const result = await axios.post(`/v2/transporter/payments/${commentId}/comment_attachments`,
        formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async uploadDocument(_, formData) {
    try {
      const result = await axios.post('/attachment', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      return result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateDocument(_, { orderNo, lorryReceipts }) {
    try {
      const result = await axios.put(`/order/${orderNo}/lorry_receipt`, { data: lorryReceipts });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async removeAttachment(_, { data }) {
    try {
      const result = await axios.delete(
        `order/${data.indent_id}/consignee/${data.consignee_id}/lr/${data.file_id}`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async filterInvoice({ commit }, params) {
    try {
      let result = [];
      if (params.page_number === 1) {
        commit('SET_LOADING', true);
      } else {
        commit('paginationCheck', true);
      }
      result = await axios('/v2/transporter/invoice', { params });

      if (result && result.data) {
        commit('SET_LIST', result.data.invoiceListData);
      }
      return result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    } finally {
      commit('paginationCheck', false);
      commit('SET_LOADING', false);
    }
  },

  async addInvoiceComment({ commit }, params) {
    try {
      const result = await axios.post(`/v2/transporter/invoice/${params.invoiceId}/comment`,
        { data: params });
      return result && result.data;
    } catch (error) {
      return false;
    }
  },

  async addInvoiceAttachment(_, formData) {
    try {
      const result = await axios.post('/v2/transporter/attachment/',
        formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async deletInvoiceAttachment(_, params) {
    const { attachmentId, attachLength } = params;
    try {
      const result = await axios.delete(`/v2/transporter/attachment/${attachmentId}`, { data: { attachLength } });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getIndentDetails(_, params) {
    try {
      params.show = 'consumed_capacity,charges,lorry_receipts,payment_discrepencies,charge_comment_attachments';
      const result = await axios('/v2/transporter/payments', { params });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async mailAnnexure(_, params) {
    try {
      const result = await axios.get(`/v2/transporter/invoice/${params.invoiceId}/mailannexure`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getIndentDetailsTimelines(_, params) {
    try {
      const result = await axios('/v2/transporter/indent_details', { params });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getIndentChargeDetails(_, params) {
    try {
      const result = await axios(`/v2/transporter/payment/charge/${params.payment_id}/${params.consignee_data_id}`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getIndentCharges({ commit }, data) {
    try {
      const params = data.params;
      params.show = 'consumed_capacity,charges,payment_discrepencies';
      const result = await axios.put('/v2/transporter/payments/charges', { params });
      if (result && result.data) {
        if (params.page_number === 1) {
          if (result.data.paymentDetails && result.data.paymentDetails.dedicatedTruckMaster) {
            commit('setDedicatedTruckMaster', result.data.paymentDetails.dedicatedTruckMaster);
          } else {
            commit('setDedicatedTruckMaster', null);
          }
        }
        if (result.data && result.data.paymentDetails && result.data.paymentDetails.payments) {
          commit('setIndentDetails', { data: result.data.paymentDetails.payments, params });
          commit('updateDepotPagination', { ...params, payment_count: result.data.paymentDetails.payments.length });
        }
        if (result.data && result.data.paymentDetails && result.data.paymentDetails.count) {
          commit('setTotalIndentCount', result.data.paymentDetails.count);
        }
        if (result.data.freight_bill) {
          commit('setFreightBillDetails', result.data.freight_bill);
        }
      }
      return result && result.data;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getInvoicePaymentsList(_, params) {
    try {
      const result = await axios.get(`/v2/transporter/invoice/${params.invoiceId}`, { params });
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getPaymentsList(_, params) {
    try {
      const result = await axios.get('/v2/transporter/payments/list', { params });
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getDepotWiseIndentCharges({ commit }, data) {
    try {
      const params = data.params;
      params.show = 'consumed_capacity,charges,payment_discrepencies';
      const result = await axios(`/v2/transporter/freightBillPaymentCharges/${data.freightBillId}`, { params });
      if (result && result.data) {
        if (params.page_number === 1) {
          if (result.data.paymentDetails && result.data.paymentDetails.dedicatedTruckMaster) {
            commit('setDedicatedTruckMaster', result.data.paymentDetails.dedicatedTruckMaster);
          } else {
            commit('setDedicatedTruckMaster', null);
          }
        }
        if (result.data.paymentDetails && result.data.paymentDetails.payments) {
          commit('setIndentDetails', { data: result.data.paymentDetails.payments, params });
          commit('updateDepotPagination', { ...params, payment_count: result.data.paymentDetails.payments.length });
        }
      }
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getFreightBillDetails({ commit }, data) {
    try {
      let result = [];
      if (data.freightBillId) {
        result = await axios(`/v2/transporter/freightBill/${data.freightBillId}`);
      } else if (data.paymentIds) {
        result = await axios.put('/v2/transporter/payments/charges/meta', { data: data.paymentIds });
      }
      if (result && result.data) {
        if (result.data && result.data.totalSystemCharges) {
          commit('setTotalSystemCharges', result.data.totalSystemCharges[0].total);
        }
        if (result.data && result.data.totalTransporterCharges) {
          commit('setTotalTransporterCharges', result.data.totalTransporterCharges[0].total);
          commit('setTansporterTotalFreightCharge', result.data.totalTransporterCharges[0].freight);
        }
        if (result.data && result.data.totalIndentCount) {
          commit('setTotalIndentCount', result.data.totalIndentCount);
        }
        if (result.data.freight_bill) {
          commit('setFreightBillDetails', result.data.freight_bill);
        }
        if (result.data && result.data.depot_details) {
          commit('setDepotDetails', result.data);
        }
        if (result.data.payment_movements) {
          commit('setPaymentMovements', result.data.payment_movements);
        }
        if (result.data.proforma_invoice_number) {
          commit('setProformaInvoiceNumber', result.data.proforma_invoice_number);
        }
      }
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async updatePaymentCharge(_, data) {
    try {
      const result = await axios.patch(`/v2/transporter/payments/${data.id}`, { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async createInvoice(_, data) {
    try {
      const result = await axios.post('/v2/transporter/invoice', { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async updateInvoice(_, { invoiceId, data }) {
    try {
      const result = await axios.put(`/v2/transporter/invoice/${invoiceId}/invoice_number`, { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getInvoiceMeta({ commit }, { invoiceId }) {
    try {
      let result = [];
      result = await axios(`/v2/transporter/invoice/${invoiceId}/meta`);
      if (result && result.meta) {
        if (result.meta && result.meta.system_total) {
          commit('setTotalSystemCharges', result.meta.system_total);
        }
        if (result.meta && result.meta.transporter_total) {
          commit('setTotalTransporterCharges', result.meta.transporter_total);
          commit('setTansporterTotalFreightCharge', result.meta.transporter_freight);
        }
        if (result.meta && result.meta.indents_count) {
          commit('setTotalIndentCount', result.meta.indents_count);
        }
        if (result.meta.freight_bill) {
          commit('setFreightBillDetails', result.meta.freight_bill);
        }
        if (result.meta.depot_details) {
          commit('setDepotDetails', result.meta);
        }
        if (result.meta.invoiceAmount) {
          commit('setInvoiceAmount', result.meta.invoiceAmount);
        }
        commit('setIsShipperChargesFeatureEnabled', !!result.meta.is_shipper_charges_feature_enabled);
        // console.log('result.meta.proforma_invoice_number === ', result.meta.proforma_invoice_number);
        // if (result.meta.proforma_invoice_number) {
        //   commit('setProformaInvoiceNumber', result.meta.proforma_invoice_number);
        // }
      }
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getDepotWiseInvoiceDetail({ commit }, { invoiceId, params }) {
    try {
      const result = await axios(`/v2/transporter/invoice/${invoiceId}`, { params });
      if (result && result.data) {
        commit('setInvoiceDetails', { ...result, params });
        if (result.data.payments) {
          commit('updateDepotPagination', { ...params, payment_count: result.data.payments.length });
        }
        if (result.data.dedicatedTruckMaster) {
          commit('setDedicatedTruckMaster', result.data.dedicatedTruckMaster);
        }
      }
      return result;
    } catch (error) {
      console.log(error);
      return HandleResponsesV2.Status(error);
    }
  },

  async getBillDiscountMasterData(_, params) {
    try {
      const result = await axios.get('/v2/transporter/invoice/billDiscount/masterData', { params });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async setInvoiceStatus(_, params) {
    try {
      let result = [];
      result = await axios.put(`/v2/transporter/invoice/${params.invoiceId}/status`,
        { data: params.data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async setInvoiceToCancel(_, params) {
    try {
      let result = [];
      result = await axios.put(`/v2/transporter/invoice/${params.invoiceId}/cancel`,
        { data: params.data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async removePaymentFromInvoice(_, { invoiceId, paymentIds }) {
    try {
      const result = await axios.delete(`/v2/transporter/invoice/${invoiceId}/payments`,
        { data: { payment_ids: paymentIds } });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async addPaymentToInvoice(_, { invoiceId, paymentIds }) {
    try {
      const result = await axios.post(`/v2/transporter/invoice/${invoiceId}/payments`,
        { data: { payment_ids: paymentIds } });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async checkPaymentJobStatus(_, { invoiceId, redisKey }) {
    try {
      const result = await axios.get(`/v2/transporter/invoice/${invoiceId}/payments/status`, { params: { redisKey } });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getEligiblePayments(_, { invoiceId, params }) {
    try {
      const result = await axios.get(`/v2/transporter/invoice/${invoiceId}/payments/eligible`, { params });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async fetchReturnedInvoices(_, params) {
    try {
      const serialized = { ...params, from_rfb_page: true };
      if (Array.isArray(serialized.status_group)) {
        serialized.status_group = serialized.status_group.join(',');
      }
      const result = await axios('/v2/transporter/invoice', { params: serialized });
      return result && result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getPrintFile(_, params) {
    const result = await axios.get(`v2/transporter/invoice/${params.invoiceNumber}/print`);
    return result;
  },

  async getFreightBillPrintFile(_, params) {
    const result = await axios.get(`v2/transporter/freightBill/${params.freightBillId}/print`);
    return result;
  },

  async invoiceExport(_, params) {
    try {
      const result = await axios({
        url: '/v2/transporter/invoice/export',
        method: 'GET',
        responseType: 'blob',
        params
      });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },


  async saveExtraKm({ commit }, params) {
    try {
      const data = { km: params.km };
      const result = await axios.put(`v2/transporter/payment/charge/${params.payment_id}`, { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async saveTransporterDebit({ commit }, data) {
    try {
      const result = await axios.post('v2/transporter/payments/debitAdjustment', { data });
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async filterFreightBill({ commit }, params) {
    try {
      let result = [];
      if (params.page_number === 1) {
        commit('SET_LOADING', true);
      } else {
        commit('paginationCheck', true);
      }
      result = await axios('/v2/transporter/freightBill/', { params });
      if (result && result.data) {
        commit('SET_LIST', result.data);
      }
      return result.data;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    } finally {
      commit('paginationCheck', false);
      commit('SET_LOADING', false);
    }
  },

  async exportFreightBill(_, params) {
    try {
      const result = await axios.get(`v2/transporter/freightBill/${params.freightBillId}/export`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async invoiceDetails(_, params) {
    try {
      const result = await axios.get(`v2/transporter/invoice/${params.invoiceId}/detail`);
      return result;
    } catch (error) {
      return HandleResponsesV2.Status(error);
    }
  },

  async getInvoiceApprovalSettings({ commit }) {
    try {
      const result = await axios.get('v2/transporter/invoice/settings');
      if (result) {
        commit('setInvoiceApprovalSettings', result);
      }
      return result;
    } catch (error) {
      return error;
    }
  },
  async getInvoiceLevelApprovalSettings({ commit }, { invoiceId }) {
    try {
      const result = await axios.get(`v2/transporter/invoice/${invoiceId}/settings`);
      if (result) {
        commit('setInvoiceLevelApprovalSettings', result);
      }
      return result;
    } catch (error) {
      return error;
    }
  },
  async getEditableCustomFields(_) {
    try {
      const result = await axios.get('v2/transporter/invoice/customFields');
      return result.data;
    } catch (err) {
      return err;
    }
  },
  async fetchAIAgentActions(_, { invoiceId }) {
    try {
      const result = await axios.get(`/v2/transporter/invoice/${invoiceId}/ai_agent_actions`);
      return result;
    } catch (error) {
      console.error('[Store] Error fetching AI Agent Actions:', error);
      return [];
    }
  },

  async fetchAIAgentActionEmailDownloadUrl(_, { invoiceId, actionId }) {
    try {
      return await axios.get(`/v2/transporter/invoice/${invoiceId}/ai_agent_actions/${actionId}/email_download`, { responseType: 'blob' });
    } catch (error) {
      return error;
    }
  },
};

export default actions;
